<template>
    <div>
        <picture
            class="smooth-filter"
            :class="{ dark: showActionButtons && type === 'package' }"
            @mouseenter="hoverOnImage"
            @mouseleave="leaveOnImage"
        >
            <source
                type="image/avif"
                :srcset="`${src2x.replace(/\.(jpg|jpeg|png|gif|bmp|tiff|webp)$/i, '.avif')}`"
                :width="width * 2 ?? 'auto'"
                :height="height * 2 ?? 'auto'"
            />
            <img
                :src="src2x.replace(/\.(jpg|jpeg|png|gif|bmp|tiff|avif)$/i, '.webp')"
                :alt="alt || ''"
                :fetchpriority="fetchpriority"
                :loading="loading"
                :width="width ?? 'auto'"
                :height="height ?? 'auto'"
            />
        </picture>

        <div
            class="container-content-actions-outer"
            v-on="type === 'package' ? { mouseenter: hoverOnImage, mouseleave: leaveOnImage } : {}"
        >
            <div :class="{ top: isPopupOpen }">
                <div
                    class="container-content-actions"
                    :class="{ open: isOpen, 'has-underlying-link': hasUnderlyingLink }"
                >
                    <div
                        class="container-action-buttons"
                        v-if="showActionButtons && type === 'package'"
                        @click.self="isOpen = true"
                        @mouseleave="isOpen = false"
                    >
                        <div class="action-button main" @touchend="touchClose">
                            <transition name="fadeFast" mode="out-in" v-if="!isOpen">
                                <span @mouseenter="imageClick">...</span>
                            </transition>
                            <transition name="fadeFast" mode="out-in" v-else-if="type === 'package' && isOpen">
                                <i class="icon icon-close"></i>
                            </transition>
                        </div>
                        <transition name="butterfly" mode="out-in">
                            <div
                                v-if="type === 'package' && isOpen && authUser"
                                class="action-button add"
                                :class="{ 'is-added': isAddedInternal }"
                                @click="toggleAdd"
                                :title="isAddedInternal ? t.frontend.remove_favorite : t.frontend.add_favorite"
                            >
                                <i class="icon" :class="actions.add.icon"></i>
                            </div>
                        </transition>
                        <transition name="butterfly" mode="out-in">
                            <div
                                v-if="type === 'package' && isOpen"
                                class="action-button fav"
                                :class="{ 'is-fav': isFavInternal }"
                                @click="toggleFav"
                                :title="isFavInternal ? t.buttons.unlike : t.buttons.like"
                            >
                                <i class="icon" :class="actions.fav.icon"></i>
                            </div>
                        </transition>
                        <transition name="butterfly" mode="out-in">
                            <div
                                v-if="type === 'package' && isOpen"
                                class="action-button view"
                                @click="triggerActionView"
                                :title="t.frontend.view"
                            >
                                <i class="icon" :class="actions.view.icon"></i>
                            </div>
                        </transition>
                    </div>
                </div>

                <ImageView
                    v-if="isPopupOpen"
                    @close="isPopupOpen = false"
                    :class="popupPosition"
                    :preview-image="previewImage !== '0' ? previewImage : false"
                />
            </div>
            <div>
                <CompatibilityPopup
                    v-if="isCompatibilitiesPopupOpen"
                    @close="closeCompatibilities"
                    :contentId="id"
                ></CompatibilityPopup>
                <BasePopup v-if="isLoginPopupOpen" @close="isLoginPopupOpen = false" class="tutkitpopup612">
                    <div>
                        <h3>{{ t.popup.fav_content_title }}</h3>
                        <h4>
                            <b>{{ t.popup.fav_content_subtitle }}</b>
                        </h4>
                        <p>{{ t.popup.fav_content_text }}.</p>

                        <div class="button-bar">
                            <BaseButton type="high" color="alternate" target="_direct" @click.prevent="routeRegister">
                                {{ t.buttons.register }}
                            </BaseButton>
                            <BaseButton type="high" @click.prevent="routeLogin">{{ t.buttons.login }}</BaseButton>
                        </div>
                    </div>
                </BasePopup>
            </div>
        </div>

        <div v-if="contentUpdate" class="content-badge update">
            {{ t.content.update }}
        </div>

        <div v-if="contentFree" class="content-badge free">
            {{ t.content.free }}
        </div>
    </div>
</template>

<script>
import { usePackageStore } from "@/frontend/package-store.ts"
import ImageView from "@/components/Frontend/ImageView.vue"
import BasePopup from "@/components/Frontend/BasePopup.vue"
import CompatibilityPopup from "@/components/Frontend/CompatibilityPopup.vue"
import BaseButton from "@/components/Buttons/BaseButton.vue"
import { openLoginRegistrationPopup } from "@/libs/helpers.ts"
const translationsData = window.translationsData

export default {
    components: { ImageView, BasePopup, CompatibilityPopup, BaseButton },
    props: [
        "id",
        "type",
        "is-fav",
        "is-added",
        "route-actions",
        "preview-image",
        "callback-fav",
        "callback-add",
        "has-underlying-link",
        "logged-in",
        "src",
        "src2x",
        "alt",
        "is-free",
        "is-update",
        "fetchpriority",
        "loading",
        "width",
        "height",
    ],
    data() {
        return {
            isOpen: false,
            showActionButtons: false,
            isAddedInternal: false,
            isFavInternal: false,
            actions: {
                add: { icon: "icon-rate-star" },
                fav: { icon: "icon-like" },
                view: { icon: "icon-eye-black" },
            },
            isPopupOpen: false,
            isCompatibilitiesPopupOpen: false,
            isLoginPopupOpen: false,
            contentFree: this.isFree ? this.isFree : false,
            contentUpdate: this.isUpdate ? this.isUpdate : false,
            popupImagePreloaded: false,
            timerId: false,
            initialX: 0,
            initialY: 0,
            popupPosition: "",
            t: translationsData.translations,
            packageStore: usePackageStore(),
            authUser: false,
            content: null
        }
    },
    async created() {
        this.isAddedInternal = this.isAdded || false
        this.isFavInternal = this.isFav || false
        await this.packageStore.fetchPackageContentsData()
        this.authUser = this.packageStore.getAuthUser()
        this.content = this.packageStore.getContentById(this.id)

        this.createListeners()
        window.addEventListener("keyup", this.closeOnESC)
        window.eventBus &&
            window.eventBus.$on("apply_is_fav", (type, id, value) => {
                if (type !== this.type || id != this.id) {
                    return
                }

                this.isFavInternal = value
                this.$forceUpdate()
            })

        window.eventBus &&
            window.eventBus.$on("apply_is_added", (type, id, value) => {
                if (type !== this.type || id != this.id) {
                    return
                }

                this.isAddedInternal = value
                this.$forceUpdate()
            })
    },
    destroyed() {
        window.removeEventListener("keyup", this.closeOnESC)
    },
    methods: {
        toggleFav(event) {
            event.stopPropagation()
            if (!this.loggedIn) {
                this.isLoginPopupOpen = true
                return
            }
            this.isFavInternal = !this.isFavInternal
            try {
                window.eventBus && window.eventBus.$emit("apply_is_fav", this.type, this.id, this.isFavInternal)
            } catch (error) {
                console.error("Error toggling favorite status:", error)
            }
        },
        hoverOnImage(e) {
            this.showActionButtons = true

            if (this.type === "content" && window.matchMedia("(min-width: 1200px)").matches) {
                this.preloadCoverImage()
                this.timerId = setTimeout(this.hoverOnImageTimeout, 50)

                let cursorX = e.clientX
                let screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
                let isLeftHalf = cursorX < screenWidth / 2

                if (isLeftHalf) {
                    this.popupPosition = "left"
                } else {
                    this.popupPosition = "right"
                }
            }
        },
        hoverOnImageTimeout() {
            this.isPopupOpen = true
            this.timerId = false

            window.addEventListener("mousemove", this.checkingMouseMovement)
        },

        checkingMouseMovement() {
            if (this.initialX === 0 && this.initialY === 0) {
                this.initialX = event.clientX
                this.initialY = event.clientY
            }

            let distanceX = Math.abs(Math.abs(event.clientX) - Math.abs(this.initialX))
            let distanceY = Math.abs(Math.abs(event.clientY) - Math.abs(this.initialY))

            if (distanceX >= 250 || distanceY >= 250) {
                this.leaveOnImage()
            }
        },

        leaveOnImage() {
            this.isOpen = false
            this.showActionButtons = false
            if (this.type === "content") {
                this.isPopupOpen = false
                this.popupPosition = ""
                window.removeEventListener("mousemove", this.checkingMouseMovement)
                this.initialX = 0
                this.initialY = 0
                if (this.timerId) {
                    clearTimeout(this.timerId)
                    this.timerId = false
                }
            }
        },
        routeLogin() {
            this.isLoginPopupOpen = false
            openLoginRegistrationPopup()
        },
        routeRegister() {
            this.isLoginPopupOpen = false
            openLoginRegistrationPopup("registration")
        },
        toggleOpen(event) {
            event.stopPropagation()
            this.isOpen = !this.isOpen
        },
        touchClose(event) {
            event.preventDefault()
            this.showActionButtons = false
            this.isOpen = false
            this.preloadCoverImage()
        },
        preloadCoverImage() {
            if (!this.popupImagePreloaded) this.popupImagePreloaded = true
        },
        showCompatibilities(event) {
            event.stopPropagation()
            this.isCompatibilitiesPopupOpen = true
            // document.body.style.overflow = 'hidden';
        },
        closeCompatibilities() {
            this.isCompatibilitiesPopupOpen = false
            // document.body.removeAttribute('style');
        },
        showImagePreview() {
            if (!this.isCompatibilitiesPopupOpen) {
                this.isPopupOpen = true
            }
        },
        async toggleAdd(event) {
            event.stopPropagation()

            if (!this.loggedIn) {
                this.isLoginPopupOpen = true
                return
            }

            try {
                window.eventBus && window.eventBus.$emit("apply_is_added", this.type, this.id, !this.isAddedInternal)
            } catch (error) {
                console.error(error)
            }
        },
        triggerActionView(event) {
            event.stopPropagation()
            this.isPopupOpen = true
        },
        createListeners() {
            this.$root.$on("apply_is_added", (type, id, value) => {
                if (type !== this.type || id != this.id) {
                    return
                }

                this.isAddedInternal = value
            })

            this.$root.$on("apply_is_fav", (type, id, value) => {
                if (type !== this.type || id != this.id) {
                    return
                }

                this.isFavInternal = value
            })
        },
        closeOnESC(event) {
            if (parseInt(event.keyCode) === 27) {
                this.isPopupOpen = false
            }
        },
        imageClick() {
            this.isOpen = true
            this.showActionButtons = true
            this.preloadCoverImage()
        },
    },
}
</script>

<style lang="scss" scoped>
.container-content-actions-outer {
    //position: absolute !important;
    top: 0;
    bottom: 0;
    width: 100%;
    height: initial !important;
    margin-bottom: 0;

    &.top {
        z-index: 1;
    }
}

.container-content-actions {
    width: 100%;
    height: 100%;
    user-select: none;

    ::after {
        display: none !important;
    }

    &.has-underlying-link {
        cursor: pointer;
    }

    .container-action-buttons {
        font-size: 1em !important;
        position: absolute;
        bottom: 14px;
        right: 11px;
        width: 105px;
        height: 105px;
        z-index: 2;

        .action-button {
            position: absolute;
            border-radius: 100px;
            width: 30px;
            height: 30px;
            background-color: var(--color-light-grey);
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            box-shadow: 0 0 0 3px rgb(0 0 0 / 10%);
            transition: transform 0.3s;

            &.is-added {
                background-color: var(--color-primary);
            }

            &.is-fav {
                background-color: var(--color-error);
            }

            &:not(.main):hover {
                transform: scale(1.25);
            }

            .icon {
                fill: var(--color-white);
                font-size: 30px;
            }

            &.main {
                box-shadow: 0 0 0 5px rgb(0 0 0 / 10%);
                text-align: center;
                background-color: var(--color-white);
                color: var(--color-grey);
                bottom: 0;
                right: 0;
                width: 50px;
                height: 50px;
                font-size: 2rem;

                > .icon {
                    filter: invert(30%);
                }

                > span {
                    top: -1px;
                    position: absolute;
                }
            }

            &.requirement,
            &.fav {
                left: 0;
                top: 70px;
                color: var(--color-white);
            }

            &.add {
                left: 70px;
                top: 0;
            }

            &.view {
                top: 20px;
                left: 20px;

                > .icon {
                    filter: invert(100%);
                }
            }
        }
    }
}

.button-bar {
    display: flex;
    justify-content: space-between;

    .button {
        width: 250px;
    }

    @media (width <= 740px) {
        flex-direction: column-reverse;

        .button {
            width: 100%;
        }
    }
}

.content-badge {
    position: absolute;
    left: 0;
    top: 0;
    width: 250px;
    z-index: 20;
    height: 25px;
    text-transform: uppercase;
    font-size: 12px;
    color: #fff;
    font-weight: 600;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    @media (width <= 576px) {
        width: 100%;
    }

    &.update {
        background-color: var(--color-warning-90-percent);
    }

    &.free {
        background-color: var(--color-alternate-90-percent);
    }
}

.smooth-filter {
    img {
        width: 100%;
    }
}
</style>
